import React, { useCallback, useEffect, useState } from 'react';
import { FormSpy } from 'react-final-form';
import { makeStyles } from '@material-ui/core/styles';
import { FormState, FORM_ERROR, FormApi } from 'final-form';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { CircularProgress } from '@material-ui/core';
import { getTransactionsArray } from 'utils/mapping/transactionCardMapper';
import api from 'api/api';
import routePaths from 'routes/routePaths';
import { OTPConfirmationModal, WarningModal, StatusModal } from 'components/modals';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import { AxiosStatus } from 'api/axiosConfig';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import { DEFAULT_ACCOUNT_TYPE, NETWORK_REQUIRED } from 'utils/constants';
import { requiredOnly, validationCallback, validateAmount } from 'utils/validation';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import downloadReceipt from 'utils/downloadReceipt';
import { IAccount } from 'store/accounts.model';
import { IApiResponse, Signatory, UserRoles } from 'utils/models';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import {
  IFormValues,
  ITransactionField,
  ITransferResponse,
  IExtraFields,
  sortArrays,
  FAB_BANK,
  sortWithScheduleArrays,
} from './BetweenAccounts.model';
import { renderTransferFieldsFrom, renderTransferFieldsTo } from './BetweenAccountsFields';

const schema = yup.object().shape({
  sourceAccount: requiredOnly('Source Account'),
  amount: validateAmount(),
  destinationAccountNo: requiredOnly('Destination Account'),
  narration: requiredOnly('Narration'),
  scheduleTransfer: yup.boolean(),
  frequency: yup.string().when('scheduleTransfer', {
    is: true,
    then: requiredOnly('Frequency'),
  }),
  startDate: yup.string().test('startDate', 'Special Date is required', (value, context) => {
    const FABValues: IFormValues = context.parent as IFormValues;
    if (FABValues?.frequency === 'specialDate') {
      return !!value;
    }
    return true;
  }),
});

interface IProps {
  accounts: IAccount[];
  handleChangeForm: () => void;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    columnGap: '24px',
    margin: 0,
    paddingTop: 16,
  },
  label: {
    fontFamily: 'var(--ff-etelka-text)',
    fontSize: 14,
    lineHeight: '24px',
    '&.Mui-disabled': {
      color: '#000',
    },
  },
});

export const getReceipt = (transactionID: number) =>
  api.get(`/transaction/receipt?trnxID=${transactionID}`).then(downloadReceipt);

const BetweenAccounts = ({ accounts, handleChangeForm }: IProps) => {
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;

  // Extras
  const INITIAL_EXTRAS: IExtraFields = {
    destinationBankName: '',
    destinationBankCode: '',
    destinationAccountName: '',
    destinationIsExistingBen: false,
    transferType: '',
  };
  const [extras, setExtras] = useState<IExtraFields>(INITIAL_EXTRAS);
  const updateExtras = useCallback(
    (update: Partial<IExtraFields>) => setExtras((current) => ({ ...current, ...current, ...update })),
    [],
  );

  // Confirmation modal state
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [previewData, setPreviewData] = useState<ITransactionField[]>([]);
  const [OTPError, setOTPError] = useState<IOTPError | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [transactionId, setTransactionId] = useState<number>(NaN);
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;
  const [simpleWarning, setSimpleWarning] = useState<string>();
  const [toggleSchedule, setToggleSchedule] = useState<boolean | null>(null);
  let mainForm!: FormApi<IFormValues, Partial<IFormValues>>;
  const validate = useCallback((values: IFormValues) => validationCallback<unknown>(schema)(values), []);

  const onChangeForm = useCallback(
    ({ values, dirty }: FormState<IFormValues>) => {
      if (dirty) {
        handleChangeForm();
      }
      const { destinationAccountNo } = values || {};
      const destAccount = accounts.find((a) => a.accountNumber === destinationAccountNo);
      if (destAccount) {
        updateExtras({
          destinationAccountName: destAccount.accountName,
          destinationBankName: FAB_BANK.name,
          destinationBankCode: FAB_BANK.code,
          destinationIsExistingBen: false,
          beneficiaryId: undefined,
        });
      } else {
        // console.error('DID NOT FIND OWN ACCOUNT');
        // todo: if destAccount not found, prompt
      }
    },
    [accounts, updateExtras, handleChangeForm],
  );

  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.dashboard);
  };

  const closeSimpleWarning = useCallback(() => setSimpleWarning(undefined), []);

  const openConfirmation = () => setConfirmationOpen(true);
  const closeConfirmation = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    } else {
      setConfirmationOpen(false);
    }
  };

  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
    closeConfirmation();
  }, []);

  const onSubmit = (values: IFormValues) => {
    const account = accounts.find((a) => a.accountNumber === values.sourceAccount);

    if (Number(account?.availableBalance) < Number(values.amount)) {
      setSubmitStatus(AxiosStatus.FAILURE);
      setErrorMsg(`Transfer failed.
      Insufficient funds to complete the transaction`);
      return;
    }
    const readyValues = {
      sourceAccount: values.sourceAccount,
      amount: values.amount,
      destinationAccountNo: values.destinationAccountNo,
      narration: values.narration,
      frequency: values.frequency === 'specialDate' ? 'SPECIAL DATE' : values.frequency?.toLocaleUpperCase(),
      startDate: values.startDate || '',
    };

    const newSortArrays = values.scheduleTransfer ? sortWithScheduleArrays : sortArrays;
    setPreviewData(getTransactionsArray(readyValues, undefined, undefined, newSortArrays));
    setSubmitStatus(AxiosStatus.PENDING);
    api
      .post('/customer/otp/generate')
      .then(() => {
        setSubmitStatus(AxiosStatus.DEFAULT);
        openConfirmation();
      })
      .catch((data) => {
        onRejectedSubmit(data);
      });
  };

  const getMessage = (role: UserRoles) => {
    switch (role) {
      case Signatory.INITIATOR:
        return 'Transfer request has been successfully sent for approval!';
      default:
        return 'Transfer Completed Successfully!';
    }
  };

  const onConfirm = useCallback(
    (values: IFormValues) => (otp: string) => {
      setErrorMsg('');
      setSubmitStatus(AxiosStatus.PENDING);

      api
        .post(`customer/otp/verify/${otp || ''}/false/${String(sessionStorage.getItem('userName'))}`)
        .then(() => {
          setSubmitStatus(AxiosStatus.PENDING);
          const sourceAccount = accounts.find((a) => a.accountNumber === values.sourceAccount);

          const requestBody = {
            ...extras,
            ...values,
            password: otp,
            passwordType: 'OTP',
            transType: 'OWN-TRANSFER',
            currencyCode: sourceAccount?.currency,
            sourceAccountType: sourceAccount?.accountType || DEFAULT_ACCOUNT_TYPE,
            isSchedule: values.scheduleTransfer,
          };
          delete requestBody.scheduleTransfer;

          api
            .post('transfer/toSelf', requestBody)
            .then((response: ITransferResponse) => {
              setSuccessMsg(response.description || getMessage('Initiator'));
              setSubmitStatus(AxiosStatus.SUCCESS);
              setTransactionId(response.transactionId);
            })
            .catch((error: IApiResponse) => {
              console.error(error);
              setErrorMsg(error.description || 'Transfer failed. Unable to complete the transaction.');
              setSubmitStatus(AxiosStatus.FAILURE);
            });
        })
        .catch((error: IOTPError) => {
          setOTPError(error);
          if (error.failureCount === 0) {
            return { [FORM_ERROR]: error.description || NETWORK_REQUIRED };
          }
          return null;
        });
    },
    [accounts, extras],
  );

  const onResend = () => api.post('/customer/otp/generate');

  const onGetReceipt = useCallback(() => getReceipt(transactionId), [transactionId]);

  const onRetryConfirm = () => setSubmitStatus(AxiosStatus.DEFAULT);

  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };
  const classes = useStyles();

  const dateFilterChange = (name: string, value?: string | null | undefined) => {
    mainForm.change('startDate', value as string);
  };

  const handleClearDateInput = (name: string, value?: string | null | undefined) => {
    mainForm.change('startDate', value as string);
  };

  const toggleScheduleTransfer = () => setToggleSchedule((prev) => !prev);

  useEffect(() => {
    if (toggleSchedule == null) {
      return;
    }

    mainForm.change('scheduleTransfer', toggleSchedule);
    if (!toggleSchedule) {
      mainForm.change('frequency', '');
      mainForm.change('startDate', '');
    }
  }, [toggleSchedule]);

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      mutators={{ changeFieldValue, setFieldTouched }}
      render={({ handleSubmit, values, form }) => {
        mainForm = mainForm || form;
        return (
          <form onSubmit={handleSubmit}>
            <FieldsLeveler label="Transfer From" dividerBottom>
              {renderTransferFieldsFrom(values, accounts, submitting)}
            </FieldsLeveler>
            <FieldsLeveler label="Transfer To">
              {renderTransferFieldsTo(
                accounts,
                values,
                submitting,
                toggleScheduleTransfer,
                toggleSchedule,
                classes,
                dateFilterChange,
                handleClearDateInput,
              )}
            </FieldsLeveler>
            <SubmitLeveler>
              <Button type="submit" disabled={submitting} fullWidth>
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Make Transfer'}
              </Button>
            </SubmitLeveler>
            <OTPConfirmationModal
              open={isConfirmationOpen}
              data={previewData}
              onClose={closeConfirmation}
              onResend={onResend}
              OTPPinError={OTPError}
              onSubmit={onConfirm(values)}
              submitStatus={submitStatus}
              onSuccess={handleOnSuccess}
              onRetry={onRetryConfirm}
              getReceipt={onGetReceipt}
              successText={successMsg}
              failureText={errorMsg}
              {...(extras.destinationIsExistingBen
                ? { disableSaveBeneficiary: true, defaultSaveBeneficiary: false }
                : {})}
            />
            <StatusModal
              open={showStatusModal}
              status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
              onClose={onCloseStatusModal}
              onRetry={onCloseStatusModal}
              onRetryText="Close"
            >
              {submitStatus === AxiosStatus.SUCCESS ? successMsg : (
                <div style={{ whiteSpace: 'pre-line' }}>{errorMsg}</div>
              )}
            </StatusModal>
            <FormSpy subscription={{ values: true, dirty: true }} onChange={onChangeForm} />
            <WarningModal open={!!simpleWarning} onClose={closeSimpleWarning} cancelName="Close">
              {simpleWarning}
            </WarningModal>
          </form>
        );
      }}
    />
  );
};

export default BetweenAccounts;
